import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, positiveQuantitySchema, quantitySchema, } from './common.js';
import { paginationMetaSchema } from './pagination.js';
const nonnegative = quantitySchema.refine((value) => !value.startsWith('-'), 'operations.errors.negative');
const time = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);
export const tourPointSchema = z
    .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    label: z.string().trim().max(200).optional(),
    departureAt: isoDateTimeSchema.optional(),
})
    .strict();
export const tourVehicleSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    capacityKg: positiveQuantitySchema.nullable().default(null),
    capacityPackages: z.number().int().positive().nullable().default(null),
})
    .strict();
export const tourWindowSchema = z
    .object({ from: time, until: time })
    .strict()
    .refine((value) => value.until > value.from, 'tours.errors.window');
export const tourCommandSchema = z
    .object({
    version: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(8).max(100),
})
    .strict();
export const tourCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(150),
    tourDate: isoDateSchema,
    driverId: idSchema,
    vehicle: tourVehicleSchema.nullable().default(null),
    startPoint: tourPointSchema.nullable().default(null),
    endPoint: tourPointSchema.nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
    idempotencyKey: z.string().trim().min(8).max(100),
})
    .strict();
export const tourAssignSchema = tourCommandSchema
    .extend({
    documentId: idSchema,
    lines: z
        .array(z.object({ sourceItemId: idSchema, quantityBase: positiveQuantitySchema }).strict())
        .min(1)
        .max(500),
    receivingWindow: tourWindowSchema.nullish(),
    durationMinutes: z.number().int().min(1).max(1440).default(15),
    notes: z.string().trim().max(2000).nullish(),
    replanStopId: idSchema.nullish(),
})
    .strict();
export const tourOrderSchema = tourCommandSchema
    .extend({ stopIds: z.array(idSchema).min(1).max(200) })
    .strict();
export const tourStopStatusSchema = tourCommandSchema
    .extend({
    status: z.enum(['EN_ROUTE', 'ARRIVED', 'FAILED']),
    reason: z.string().trim().max(2000).optional(),
})
    .strict();
export const tourProofSchema = tourCommandSchema
    .extend({
    recipientName: z.string().trim().min(1).max(150),
    lines: z
        .array(z
        .object({
        lineId: idSchema,
        deliveredQuantityBase: nonnegative,
        damagedQuantityBase: nonnegative.default('0'),
        discrepancyReason: z.string().trim().max(2000).optional(),
    })
        .strict())
        .min(1)
        .max(500),
    signatureImage: z
        .string()
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/)
        .max(262144)
        .nullable()
        .default(null),
    photoAttachmentIds: z.array(idSchema).max(10).default([]),
    notes: z.string().trim().max(2000).nullable().default(null),
    gps: z
        .object({ latitude: z.number().min(-90).max(90), longitude: z.number().min(-180).max(180) })
        .strict()
        .nullable()
        .default(null),
    containerReturns: z
        .array(z
        .object({
        packagingTypeId: idSchema,
        quantity: positiveQuantitySchema,
        condition: z.enum(['GOOD', 'DAMAGED']),
        sourceIdentity: z.string().trim().min(8).max(200),
    })
        .strict())
        .max(50)
        .default([]),
})
    .strict();
export const tourSettingsSchema = z
    .object({
    gpsEnabled: z.boolean().default(false),
    defaultDurationMinutes: z.number().int().min(1).max(1440).default(15),
    timeZone: z
        .string()
        .default('Europe/Berlin')
        .refine((value) => {
        try {
            new Intl.DateTimeFormat('en', { timeZone: value });
            return true;
        }
        catch {
            return false;
        }
    }),
    routingProvider: z.enum(['NONE', 'OSRM']).default('NONE'),
    maxRoutingStops: z.number().int().min(2).max(100).default(50),
})
    .strict();
export const tourLineSchema = z.object({
    id: idSchema,
    documentId: idSchema,
    sourceItemId: idSchema,
    name: z.string(),
    unitCode: z.string(),
    packagingFactor: z.string(),
    plannedQuantityBase: z.string(),
    deliveredQuantityBase: z.string(),
    damagedQuantityBase: z.string(),
    discrepancyReason: z.string().nullable(),
});
export const deliveryStopSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    shippingAddressId: idSchema.nullable(),
    position: z.number().int(),
    status: z.enum(['PLANNED', 'EN_ROUTE', 'ARRIVED', 'DELIVERED', 'PARTIAL', 'FAILED']),
    version: z.number().int(),
    address: z.record(z.unknown()),
    receivingWindow: tourWindowSchema.nullable(),
    durationMinutes: z.number().int(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    notes: z.string().nullable(),
    failureReason: z.string().nullable(),
    lines: z.array(tourLineSchema),
    proof: z
        .object({
        id: idSchema,
        recipientName: z.string(),
        confirmedAt: isoDateTimeSchema,
        signatureImage: z.string().nullable(),
        photoAttachmentIds: z.array(idSchema),
        notes: z.string().nullable(),
        gps: z.object({ latitude: z.number(), longitude: z.number() }).nullable(),
        containerEntryIds: z.array(idSchema),
    })
        .nullable(),
});
export const tourConflictSchema = z.object({
    code: z.enum([
        'MISSING_COORDINATES',
        'ADDRESS_INCOMPLETE',
        'ROUTING_NOT_CONFIGURED',
        'MISSING_DEPARTURE',
        'TIME_WINDOW',
        'CAPACITY',
        'MISSING_WEIGHT',
        'MISSING_PACKAGES',
        'ROUTE_UNREACHABLE',
    ]),
    stopId: z.string().nullable(),
    detail: z.string().nullable().default(null),
});
export const tourRoutePlanSchema = z.object({
    provider: z.string(),
    optimized: z.boolean(),
    calculatedAt: isoDateTimeSchema,
    durations: z.array(z.array(z.number().nonnegative().nullable())),
    arrivals: z.array(z.object({ stopId: idSchema, arrivalMinute: z.number(), departureMinute: z.number() })),
    conflicts: z.array(tourConflictSchema),
});
export const deliveryTourSchema = z.object({
    id: idSchema,
    name: z.string(),
    tourDate: isoDateSchema,
    driverId: idSchema,
    driverName: z.string(),
    status: z.enum(['PLANNED', 'ACTIVE', 'COMPLETED', 'CANCELLED']),
    version: z.number().int(),
    vehicle: tourVehicleSchema.nullable(),
    startPoint: tourPointSchema.nullable(),
    endPoint: tourPointSchema.nullable(),
    routePlan: tourRoutePlanSchema.nullable(),
    notes: z.string().nullable(),
    stops: z.array(deliveryStopSchema),
    events: z.array(z.object({
        id: idSchema,
        type: z.string(),
        stopId: idSchema.nullable(),
        createdAt: isoDateTimeSchema,
        createdById: idSchema,
        details: z.record(z.unknown()),
    })),
});
export const deliveryTourListSchema = z.object({
    data: z.array(deliveryTourSchema),
    meta: paginationMetaSchema,
});
export const tourLookupsSchema = z.object({
    drivers: z.array(z.object({ id: idSchema, name: z.string() })),
    sources: z.array(z.object({
        id: idSchema,
        number: z.string(),
        type: z.enum(['ORDER', 'DELIVERY_NOTE']),
        customerId: idSchema,
        customerName: z.string(),
        shippingAddressId: idSchema.nullable(),
        address: z.record(z.unknown()),
        lines: z.array(z.object({
            id: idSchema,
            name: z.string(),
            unitCode: z.string(),
            quantityBase: z.string(),
            remainingQuantityBase: z.string(),
            packagingFactor: z.string(),
        })),
    })),
});
export const tourSettingsResponseSchema = z.object({
    settings: tourSettingsSchema,
    version: z.number().int().positive(),
    routingConfigured: z.boolean(),
});
export const tourSettingsWriteSchema = tourCommandSchema.extend({ settings: tourSettingsSchema });
export const tourDriverContextSchema = z.object({
    gpsEnabled: z.boolean(),
    containerTypes: z.array(z.object({
        id: idSchema,
        code: z.string(),
        returnConditions: z.array(z.enum(['GOOD', 'DAMAGED'])),
    })),
    photos: z.array(z.object({ id: idSchema, originalName: z.string() })),
});
//# sourceMappingURL=delivery-tour.js.map